import { useState, ChangeEvent, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import CountryPrefixInput from "../CountryPrefixInput";
import { useNavigate } from "react-router-dom";

declare global {
  interface Window {
    grecaptcha: {
      enterprise: {
        execute: (
          siteKey: string,
          options: { action: string }
        ) => Promise<string>;
        ready: (callback: () => void) => void;
      };
    };
  }
}

type ContactFormProps = {
  showSuccess?: boolean;
  className?: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  footer?: boolean;
  shouldOpenGuide?: boolean;
  context?: "risk-profiler" | "default";
};

const POLICY_URLS = {
  terms:
    "https://documents.apollofinancial.ie/polices_pdfs/Terms-of-Business-31st-July-2025.pdf",
  privacy:
    "https://documents.apollofinancial.ie/polices_pdfs/Data-Protection-Policy-Updated.pdf",
};

const API_BASE_URL =
  window._env_?.VITE_API_BASE_URL !== "${VITE_API_BASE_URL}"
    ? window._env_?.VITE_API_BASE_URL
    : "/api";

const captchaSiteKey =
  window._env_?.VITE_CAPTCHA_SITE_KEY !== "${VITE_CAPTCHA_SITE_KEY}"
    ? window._env_.VITE_CAPTCHA_SITE_KEY
    : import.meta.env.VITE_CAPTCHA_SITE_KEY;

const ContactForm = ({
  onSuccess,
  onError,
  footer,
  shouldOpenGuide = false,
  context = "default",
}: ContactFormProps) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    interestedProducts:
      context === "risk-profiler"
        ? ["Pension & Retirement", "Investments"]
        : [],
    agreedToTerms: false,
  });
  const [countryPrefix, setCountryPrefix] = useState("+353");

  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    products: "",
    terms: "",
  });
  const navigate = useNavigate();

  // const isProdDomain =
  //   window.location.hostname === "apollofinancial.ie" ||
  //   window.location.hostname === "www.apollofinancial.ie";

  // const isLocalOrProd = [
  //   "localhost",
  //   "dev01.apollofinancial.ie",
  // ].includes(window.location.hostname);

  // if (!isLocalOrProd) return null;

  useEffect(() => {
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${captchaSiteKey}`;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
      const badge = document.querySelector(".grecaptcha-badge") as HTMLElement;
      if (badge) badge.remove();
    };
  }, []);

  const validateForm = () => {
    const newErrors = {
      products:
        formData.interestedProducts.length === 0
          ? "Please select at least one product"
          : "",
      terms: !formData.agreedToTerms
        ? "Please agree to the Terms of Business and Privacy Policy above to continue"
        : "",
    };

    setErrors(newErrors);
    return !newErrors.products && !newErrors.terms;
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (type === "checkbox") {
      if (name === "agreedToTerms") {
        setFormData((prev) => ({ ...prev, agreedToTerms: checked }));
        if (checked) {
          setErrors((prev) => ({ ...prev, terms: "" }));
        }
      } else {
        const product = value;
        setFormData((prev) => ({
          ...prev,
          interestedProducts: checked
            ? [...prev.interestedProducts, product]
            : prev.interestedProducts.filter((p) => p !== product),
        }));
        if (checked || formData.interestedProducts.length > 1) {
          setErrors((prev) => ({ ...prev, products: "" }));
        }
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({ products: "", terms: "" });

    if (!validateForm()) return;
    setIsLoading(true);

    try {
      if (!window.grecaptcha?.enterprise) {
        throw new Error("CAPTCHA not loaded");
      }

      await new Promise<void>((resolve) =>
        window.grecaptcha.enterprise.ready(resolve)
      );

      const captchaToken = await window.grecaptcha.enterprise.execute(
        captchaSiteKey,
        {
          action: "CONTACT_FORM_SUBMIT",
        }
      );

      const transformedProducts = formData.interestedProducts.map((product) =>
        product.toLowerCase().replace(/ & /g, "_").replace(/ /g, "_")
      );

      // Combine country prefix with phone number (remove any leading 0 from the phone number)
      const fullPhoneNumber = `${countryPrefix}${formData.phone.replace(
        /^0+/,
        ""
      )}`;
      if (context === "risk-profiler") {
        formData.message = "Submitted via Risk Profiler Tool";
      }

      const payload = {
        name: formData.name,
        email: formData.email,
        phone: fullPhoneNumber,
        message: formData.message,
        product_selected: transformedProducts.join(", "),
        g_recaptcha_response: captchaToken,
        source: "apollo",
      };

      const response = await fetch(
        `${API_BASE_URL}/external/api/contact-form/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error("Network response was not ok");
      // if (isProdDomain) {
      //   const trustpilotInit = document.createElement("script");
      //   trustpilotInit.type = "text/javascript";
      //   trustpilotInit.innerHTML = `
      //   window.Trustpilot = window.Trustpilot || {};
      //   window.Trustpilot.invitation = {
      //   recipientEmail: "${formData.email}",
      //   recipientName: "${formData.name}",
      //   referenceId: "${formData.name}-${Date.now()}",
      //   locale: "en-IE",
      //   source: "InvitationScript",
      //   preferredSendTime: "now"
      // };`;
      //   document.body.appendChild(trustpilotInit);

      //   const trustpilotLoader = document.createElement("script");
      //   trustpilotLoader.src = "https://invitejs.trustpilot.com/tp.min.js";
      //   trustpilotLoader.async = true;
      //   trustpilotLoader.id = "trustpilot-script";
      //   document.body.appendChild(trustpilotLoader);
      // } else {
      //   console.log(
      //     "Skipping Trustpilot invite on non-production domain:",
      //     window.location.hostname
      //   );
      // }

      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 5000);
      if (context !== "risk-profiler") {
        navigate("/thank-you/", { state: { fromForm: true } });
      }
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
        interestedProducts:
          context === "risk-profiler"
            ? ["Pension & Retirement", "Investments"]
            : [],
        agreedToTerms: false,
      });
      onSuccess?.();
      window.scrollTo(0, 0);
    } catch (error) {
      console.error("Error submitting form:", error);
      onError?.(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = () => {
    return (
      formData.name.trim() !== "" &&
      formData.email.trim() !== "" &&
      formData.phone.trim() !== "" &&
      (context === "risk-profiler" || formData.message.trim() !== "") &&
      (context === "risk-profiler" || formData.interestedProducts.length > 0) &&
      formData.agreedToTerms
    );
  };

  useEffect(() => {
    if (submitted && shouldOpenGuide) {
      const timer = setTimeout(() => {
        window.open(
          "https://documents.apollofinancial.ie/Learning%20Material/15%20Minute%20Plan%20Retirement.pdf",
          "_blank"
        );
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [submitted, shouldOpenGuide]);

  return (
    <form
      onSubmit={handleSubmit}
      className={footer ? "contact-form-footer" : "contact-form"}
    >
      <div
        className={footer ? "form-group-footer" : "form-group"}
        style={{ position: "relative" }}
      >
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          pattern="^[A-Za-z]+(\s+[A-Za-z]+)+$"
          title="Please enter your full name"
          required
          style={{ width: "100%", paddingTop: formData.name ? "12px" : "12px" }}
        />
        {!formData.name && (
          <div
            style={{
              position: "absolute",
              top: "10px",
              left: "12px",
              color: "#6b7280",
              pointerEvents: "none",
            }}
          >
            Your Full Name <span style={{ color: "red" }}>*</span>
          </div>
        )}
      </div>

      <div
        className={footer ? "form-group-footer" : "form-group"}
        style={{ position: "relative" }}
      >
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          style={{ width: "100%", paddingTop: formData.name ? "12px" : "12px" }}
        />
        {!formData.email && (
          <div
            style={{
              position: "absolute",
              top: "10px",
              left: "12px",
              color: "#6b7280",
              pointerEvents: "none",
            }}
          >
            Your Email <span style={{ color: "red" }}>*</span>
          </div>
        )}
      </div>

      <div
        className={footer ? "form-group-footer" : "form-group"}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          width: "100%",
          position: "relative",
        }}
      >
        <CountryPrefixInput
          onPrefixChange={setCountryPrefix}
          selectedPrefix={countryPrefix}
        />
        <div style={{ position: "relative", flex: 1 }}>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            pattern="[0-9]{7,15}"
            title="Please enter a valid phone number"
            style={{
              width: "100%",
              paddingTop: formData.phone ? "12px" : "12px",
            }}
          />
          {!formData.phone && (
            <div
              style={{
                position: "absolute",
                top: "10px",
                left: "12px",
                color: "#6b7280",
                pointerEvents: "none",
              }}
            >
              Your Mobile Number <span style={{ color: "red" }}>*</span>
            </div>
          )}
        </div>
      </div>
      {context !== "risk-profiler" && (
        <div
          className={footer ? "form-group-footer" : "form-group"}
          style={{ position: "relative" }}
        >
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows={4}
            style={{
              resize: "vertical",
              width: "100%",
              paddingTop: "10px",
            }}
          />
          {!formData.message && (
            <div
              style={{
                position: "absolute",
                top: "10px",
                left: "12px",
                color: "#6b7280",
                pointerEvents: "none",
              }}
            >
              Your Message <span style={{ color: "red" }}>*</span>
            </div>
          )}
        </div>
      )}
      {context !== "risk-profiler" && (
        <>
          <h4
            style={{ display: "flex", marginTop: "0px", marginBottom: "0rem" }}
          >
            Which products are you interested in?&nbsp;
            <span style={{ color: "red" }}>*</span>
          </h4>
          <div className={footer ? "checkbox-group-footer" : "checkbox-group"}>
            {[
              "Full Financial Review",
              "Pension & Retirement",
              "Investments",
              "Life & Protection",
            ].map((product) => (
              <label
                key={product}
                style={{ width: "max-content", cursor: "pointer" }}
                className={footer ? "checkbox-label-footer" : "checkbox-label"}
              >
                <input
                  type="checkbox"
                  name="interestedProducts"
                  value={product}
                  checked={formData.interestedProducts.includes(product)}
                  onChange={handleChange}
                />
                {!footer && <span className="checkmark"></span>}
                {product}
              </label>
            ))}
          </div>
        </>
      )}
      {errors.products && (
        <p style={{ color: "rgb(218 14 14)", marginBottom: "-10px" }}>
          {errors.products}
        </p>
      )}

      <div className={footer ? "terms-agreement-footer" : "terms-agreement"}>
        <label className={footer ? "checkbox-label-footer" : "checkbox-label"}>
          <input
            type="checkbox"
            name="agreedToTerms"
            checked={formData.agreedToTerms}
            onChange={handleChange}
          />
          {!footer && <span className="checkmark"></span>}
          <span className="terms-text">
            I acknowledge that I have read and agree to the{" "}
            <a
              href={POLICY_URLS.terms}
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Business
            </a>{" "}
            and{" "}
            <a
              href={POLICY_URLS.privacy}
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            <span style={{ color: "red", marginLeft: "2px" }}>*</span>
          </span>
        </label>
      </div>
      {errors.terms && (
        <p style={{ color: "rgb(218 14 14)", marginTop: "0px" }}>
          {errors.terms}
        </p>
      )}

      <button
        type="submit"
        className={footer ? "submit-btn-footer" : "submit-btn"}
        disabled={isLoading || !isFormValid()}
      >
        {isLoading
          ? "Sending..."
          : context === "risk-profiler"
          ? "Show Results"
          : "Send Message"}
        {!isLoading && <FontAwesomeIcon icon={faArrowRight} />}
      </button>
    </form>
  );
};

export default ContactForm;
